<template>
  <div>
    <StartWorkCheckList
      v-if="!showHaltMessage"
      :title="t(useTranslationPath(callerPath, 'header'))"
      :description="t(useTranslationPath(callerPath, 'description'))"
      :list="workOrderChecks"
      :action-label="t(useTranslationPath(callerPath, 'checkIn'))"
      :is-loading
      @click="goTo"
      @action-click="processWorkOrderChecks"
    />
    <EmptyContainer v-else :message="t(useTranslationPath(callerPath, 'haltMessage'))">
      <template #actions>
        <EButton
          block
          :label="t(useTranslationPath(callerPath, 'endVisit'))"
          variant="secondary"
          @click="endVisit"
        />
      </template>
    </EmptyContainer>
  </div>
</template>

<script setup>
import EmptyContainer from '@/components/EmptyContainer.vue'
import StartWorkCheckList from '@/components/WorkOrder/StartWorkCheck/StartWorkCheckList.vue'
import { useTranslationPath } from '@/composables/useTranslationPath.js'
import { isAccessCodeEnabled, isLocationEnabled } from '@/composables/useVerificationMethod.js'
import { useAppStore } from '@/stores/AppStore.js'
import { useAuthStore } from '@/stores/authenticated/AuthStore.js'
import { useEngineerStore } from '@/stores/authenticated/EngineerStore.js'
import { useStartWorkCheckStore } from '@/stores/authenticated/StartWorkCheckStore.js'
import { useWorkOrderStore } from '@/stores/authenticated/WorkOrderStore'
import { EButton } from '@expansivefm/eds'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'

const { t } = useI18n()

const router = useRouter()
const appStore = useAppStore()
const authStore = useAuthStore()
const engineerStore = useEngineerStore()
const workOrderStore = useWorkOrderStore()
const startWorkCheckStore = useStartWorkCheckStore()

const callerPath = 'views.authenticated.workOrders.startWorkChecks.index'

const showHaltMessage = ref(false)
const isLoading = ref(false)

const workOrderChecks = computed(() => {
  return startWorkCheckStore.checkTasks.map((section, index) => {
    return {
      ...section,
      status: allTasksCompleted(index) ? 'completed' : 'required',
      statusVariant: allTasksCompleted(index) ? 'success' : 'warning',
    }
  })
})

onMounted(async () => {
  appStore.$patch({ showBackButton: false })

  if (!workOrderStore.workOrder.workflow_start_work_checks_required) {
    await router.push({ name: 'work-order.detail' })
  }

  if (!startWorkCheckStore.hasStartedChecks) {
    await Promise.all([
      startWorkCheckStore.fetchHazards(),
      startWorkCheckStore.fetchTasks(workOrderStore.workOrder.id),
    ])

    startWorkCheckStore.$patch({ hasStartedChecks: true })
  }
})

const allTasksCompleted = (index) => {
  const checkSection = startWorkCheckStore.checkTasks.at(index)

  if (index === 1) {
    const hazardChecks = checkSection.hazards.map((hazard) => {
      const tasks = startWorkCheckStore.checkTasks
        .at(1)
        .tasks.filter((task) => task.hazard_id === hazard.id)

      if (hazard.value !== undefined && tasks.length === 0) {
        return { value: true }
      } else if (hazard.value === true && tasks.length > 0) {
        return { value: tasks.every((task) => task.value !== null) }
      }

      return { value: false }
    })

    if (hazardChecks.length === 0) {
      return false
    }

    return hazardChecks.every((hazard) => hazard.value === true)
  }

  return checkSection.tasks.every((task) => {
    if ('value' in task) {
      return task.value !== null
    }

    return false
  })
}

const goTo = (index) => {
  const section = ['induction', 'hazards', 'safe-to-proceed']

  router.push({
    name: `work-order.start-work-checks.${section.at(index)}`,
    params: {
      workOrderId: workOrderStore.workOrder.id,
    },
  })
}

const processWorkOrderChecks = async () => {
  isLoading.value = true

  try {
    const startWorkCheck = await createWorkOrderStartWorkCheck()

    startWorkCheckStore.$patch({ check: startWorkCheck })

    const updateTasks = []

    startWorkCheckStore.checkTasks?.forEach((startWorkCheckTask) => {
      startWorkCheckTask.tasks.forEach((task) => {
        const checkIndex = startWorkCheckStore.check.workOrderStartWorkCheckTasks.findIndex(
          (t) => task.id === t.start_work_check_task_id,
        )

        if (checkIndex !== -1) {
          const check = startWorkCheckStore.check.workOrderStartWorkCheckTasks[checkIndex]
          const answer = task.value

          if (answer !== undefined && answer !== null) {
            updateTasks.push(
              startWorkCheckStore.updateStartWorkCheckTask(check.id, answer ? 'YES' : 'NO'),
            )
          }
        }
      })
    })

    await Promise.all(updateTasks)

    await startWorkCheckStore.updateStartWorkCheck(startWorkCheckStore.check.id)

    await engineerStore.updateSession(engineerStore.session.id, {
      workOrderStartWorkCheck: startWorkCheck.id,
    })

    if (startWorkCheckStore.checkTasks.at(-1).tasks.some((task) => task.value === false)) {
      showHaltMessage.value = true
    } else {
      await router.push({
        name: 'work-order.detail',
        params: {
          workOrderId: workOrderStore.workOrder.id,
        },
      })
    }
  } catch (error) {
    isLoading.value = false

    toast(t(useTranslationPath(callerPath, 'fail')), {
      autoClose: 5000,
      position: toast.POSITION.BOTTOM_CENTER,
      type: 'error',
    })
  }
}

const createWorkOrderStartWorkCheck = async () => {
  const hazards = startWorkCheckStore.checkTasks.at(1).hazards

  const { data } = await startWorkCheckStore.createStartWorkCheck({
    workOrder: workOrderStore.workOrder.id,
    hazards_start: hazards
      .filter((hazard) => hazard.value === true && hazard.disabled === true)
      .map((hazard) => ({
        id: hazard.id,
        name: hazard.name,
      })),
    hazards_end: hazards
      .filter((hazard) => hazard.value === true)
      .map((hazard) => ({
        id: hazard.id,
        name: hazard.name,
      })),
  })

  return data
}

const endVisit = async () => {
  let sessionData = {
    status: 'CLOSED',
    closure_reason: 'ANOTHER_VISIT_REQUIRED',
    visit_notes: 'Start work checks indicate that it is not safe to proceed.',
    workOrderStartWorkCheck: startWorkCheckStore.check.id,
    location_verification_method_check_out: null,
  }

  if (
    (isLocationEnabled('ENGINEER_APP_CHECK_IN_METHOD') ||
      isAccessCodeEnabled('ENGINEER_APP_CHECK_IN_METHOD')) &&
    engineerStore.verificationData.checkIn.method !== null
  ) {
    sessionData = {
      ...sessionData,
      location_verification_method_check_out: engineerStore.verificationData.checkIn.method,
    }

    if (engineerStore.verificationData.checkIn.method === 'GEOLOCATION') {
      sessionData = {
        ...sessionData,
        geolocation_check_out: engineerStore.verificationData.checkIn.data,
      }
    }
  }

  await engineerStore.updateSession(engineerStore.session.id, sessionData).then(async () => {
    await authStore.logout()

    await router.push({ name: 'index' })
  })
}
</script>
